/**
 * A reader-facing sentence for a failed `getInsightsPayload` call.
 *
 * The callable's own messages (`failed-precondition`) are written for the
 * screen and pass through. The SDK's generic codes are translated. `internal`
 * has two causes that the browser cannot tell apart: the function is not there
 * at all (a preview channel before the first release — the failed cross-origin
 * fetch carries no error body), or it is there and its BigQuery read failed
 * (the callable deliberately returns no detail). The sentence covers both and
 * points at the one place that does tell them apart.
 */
export const describePayloadError = (error: unknown): string => {
  const code =
    typeof error === 'object' && error !== null && 'code' in error
      ? String((error as { code: unknown }).code)
      : ''
  const message =
    error instanceof Error && error.message ? error.message : String(error)

  switch (code) {
    case 'functions/internal':
      return (
        'The dashboard’s function did not answer. Either it is not deployed ' +
        'to this environment yet (a preview channel gets it with the next ' +
        'release, not with the preview), or it could not read its data — ' +
        'the function’s logs say which.'
      )
    case 'functions/not-found':
      return 'The dashboard’s function is not deployed to this environment.'
    case 'functions/permission-denied':
      return 'This account may not read the dashboard.'
    case 'functions/unauthenticated':
      return 'Sign in again — the session has expired.'
    case 'functions/failed-precondition':
      return message
    default:
      return `The numbers could not be loaded. ${message}`
  }
}
